import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../ui/card";
import { Button } from "../../../ui/button";
import { Input } from "../../../ui/input";
import { Label } from "../../../ui/label";
import { Textarea } from "../../../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../ui/select";
import { Switch } from "../../../ui/switch";
import { toast } from "sonner";
import { ArrowLeft, Save, Plus, X, Loader2, Upload, Image as ImageIcon } from "lucide-react";
import AdminNavbar from "../../../AdminNavbar";
import { useOptimisticDoctors } from "../../../../hooks/useOptimisticUpdates";
import { doctorService } from "../../../../services";

const AdminDoctorCreate = () => {
  const navigate = useNavigate();
  const { createDoctorOptimistic } = useOptimisticDoctors();
  const [formData, setFormData] = useState({
    fullName: "",
    professionalTitle: "Dr.",
    specialty: "",
    mainCategory: "",
    experience: "",
    consultationFees: "",
    rating: "",
    about: "",
    contactNumber: "",
    qualifications: [{ degree: "" }],
    cities: [],
    employeeCode: "",
    status: "active",
    isVisitingDoctor: false,
    isHospitalDoctor: false,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [profileImage, setProfileImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const [newCity, setNewCity] = useState("");

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleArrayField = (field, action, value = null, index = null) => {
    setFormData(prev => {
      const currentArray = prev[field] || [];
      let newArray;

      switch (action) {
        case 'add':
          newArray = [...currentArray, value];
          break;
        case 'remove':
          newArray = currentArray.filter((_, i) => i !== index);
          break;
        default:
          newArray = currentArray;
      }

      return {
        ...prev,
        [field]: newArray
      };
    });
  };

  const handleQualificationChange = (index, field, value) => {
    setFormData(prev => {
      const newQualifications = [...prev.qualifications];
      newQualifications[index] = {
        ...newQualifications[index],
        [field]: value
      };
      return {
        ...prev,
        qualifications: newQualifications
      };
    });
  };

  const addQualification = () => {
    setFormData(prev => ({
      ...prev,
      qualifications: [...prev.qualifications, { degree: "" }]
    }));
  };

  const removeQualification = (index) => {
    setFormData(prev => ({
      ...prev,
      qualifications: prev.qualifications.filter((_, i) => i !== index)
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error("Please select a valid image file");
        return;
      }
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image size should be less than 5MB");
        return;
      }
      setProfileImage(file);
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setProfileImage(null);
    setImagePreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      // Prepare data for submission
      const submitData = {
        fullName: formData.fullName,
        professionalTitle: formData.professionalTitle,
        specialty: formData.specialty,
        mainCategory: formData.mainCategory,
        experience: formData.experience,
        consultationFees: parseFloat(formData.consultationFees) || 0,
        rating: parseFloat(formData.rating) || 0,
        about: formData.about,
        contactNumber: formData.contactNumber,
        qualifications: formData.qualifications.filter(q => q.degree),
        cities: formData.cities,
        employeeCode: formData.employeeCode,
        status: formData.status,
        isVisitingDoctor: formData.isVisitingDoctor,
        isHospitalDoctor: formData.isHospitalDoctor,
      };

      // If image is provided, use doctorService directly with image
      if (profileImage) {
        const result = await doctorService.createDoctor(submitData, profileImage);
        toast.success("Doctor created successfully!");
        navigate("/admin/doctors");
      } else {
        // Create doctor with optimistic update (no image)
        await createDoctorOptimistic(submitData);
        navigate("/admin/doctors");
      }
    } catch (error) {
      console.error("Error creating doctor:", error);
      toast.error(error.message || "Failed to create doctor. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNavbar />
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <div className="flex items-center gap-4">
            <Link to="/admin/doctors">
              <Button variant="outline" size="sm">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Doctors
              </Button>
            </Link>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Create New Doctor</h1>
              <p className="text-gray-600 mt-2">Add a new healthcare professional to the platform</p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Information */}
            <div className="lg:col-span-2 space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Basic Information</CardTitle>
                  <CardDescription>
                    Essential details about the doctor
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="professionalTitle">Professional Title *</Label>
                      <Select
                        value={formData.professionalTitle}
                        onValueChange={(value) => handleInputChange("professionalTitle", value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select title" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Dr.">Dr.</SelectItem>
                          <SelectItem value="Prof.">Prof.</SelectItem>
                          <SelectItem value="Mr.">Mr.</SelectItem>
                          <SelectItem value="Ms.">Ms.</SelectItem>
                          <SelectItem value="Mrs.">Mrs.</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="fullName">Full Name *</Label>
                      <Input
                        id="fullName"
                        value={formData.fullName}
                        onChange={(e) => handleInputChange("fullName", e.target.value)}
                        placeholder="John Doe"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="employeeCode">Employee Code</Label>
                      <Input
                        id="employeeCode"
                        value={formData.employeeCode}
                        onChange={(e) => handleInputChange("employeeCode", e.target.value)}
                        placeholder="EMP001"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="contactNumber">Contact Number *</Label>
                      <Input
                        id="contactNumber"
                        value={formData.contactNumber}
                        onChange={(e) => handleInputChange("contactNumber", e.target.value)}
                        placeholder="+1 (555) 123-4567"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="specialty">Specialty *</Label>
                      <Select
                        value={formData.specialty}
                        onValueChange={(value) => handleInputChange("specialty", value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select specialty" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="cardiology">Cardiology</SelectItem>
                          <SelectItem value="dermatology">Dermatology</SelectItem>
                          <SelectItem value="neurology">Neurology</SelectItem>
                          <SelectItem value="orthopedics">Orthopedics</SelectItem>
                          <SelectItem value="pediatrics">Pediatrics</SelectItem>
                          <SelectItem value="psychiatry">Psychiatry</SelectItem>
                          <SelectItem value="surgery">Surgery</SelectItem>
                          <SelectItem value="ophthalmology">Ophthalmology</SelectItem>
                          <SelectItem value="dentistry">Dentistry</SelectItem>
                          <SelectItem value="gynecology">Gynecology</SelectItem>
                          <SelectItem value="urology">Urology</SelectItem>
                          <SelectItem value="pulmonology">Pulmonology</SelectItem>
                          <SelectItem value="laparoscopy">Laparoscopy</SelectItem>
                          <SelectItem value="medicine">Medicine</SelectItem>
                          <SelectItem value="nephrology">Nephrology</SelectItem>
                          <SelectItem value="gastro">Gastro</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="mainCategory">Main Category *</Label>
                      <Select
                        value={formData.mainCategory}
                        onValueChange={(value) => handleInputChange("mainCategory", value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select main category" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="cardiology">Cardiology</SelectItem>
                          <SelectItem value="dermatology">Dermatology</SelectItem>
                          <SelectItem value="neurology">Neurology</SelectItem>
                          <SelectItem value="orthopedics">Orthopedics</SelectItem>
                          <SelectItem value="pediatrics">Pediatrics</SelectItem>
                          <SelectItem value="psychiatry">Psychiatry</SelectItem>
                          <SelectItem value="surgery">Surgery</SelectItem>
                          <SelectItem value="ophthalmology">Ophthalmology</SelectItem>
                          <SelectItem value="dentistry">Dentistry</SelectItem>
                          <SelectItem value="gynecology">Gynecology</SelectItem>
                          <SelectItem value="urology">Urology</SelectItem>
                          <SelectItem value="pulmonology">Pulmonology</SelectItem>
                          <SelectItem value="laparoscopy">Laparoscopy</SelectItem>
                          <SelectItem value="medicine">Medicine</SelectItem>
                          <SelectItem value="nephrology">Nephrology</SelectItem>
                          <SelectItem value="gastro">Gastro</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="experience">Years of Experience *</Label>
                      <Input
                        id="experience"
                        type="number"
                        value={formData.experience}
                        onChange={(e) => handleInputChange("experience", e.target.value)}
                        placeholder="10"
                        min="0"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="consultationFees">Consultation Fees (₹) *</Label>
                      <Input
                        id="consultationFees"
                        type="number"
                        value={formData.consultationFees}
                        onChange={(e) => handleInputChange("consultationFees", e.target.value)}
                        placeholder="1000"
                        min="0"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="rating">Rating *</Label>
                      <Input
                        id="rating"
                        type="number"
                        value={formData.rating}
                        onChange={(e) => handleInputChange("rating", e.target.value)}
                        placeholder="4.5"
                        min="0"
                        max="5"
                        step="0.1"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="status">Status</Label>
                      <Select
                        value={formData.status}
                        onValueChange={(value) => handleInputChange("status", value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="inactive">Inactive</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="about">About Doctor *</Label>
                    <Textarea
                      id="about"
                      value={formData.about}
                      onChange={(e) => handleInputChange("about", e.target.value)}
                      placeholder="Brief description about the doctor's expertise and approach"
                      rows={4}
                      required
                    />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Qualifications</CardTitle>
                  <CardDescription>
                    Academic qualifications and degrees
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {formData.qualifications.map((qualification, index) => (
                    <div key={index} className="p-4 border rounded-lg space-y-4">
                      <div className="flex justify-between items-center">
                        <h4 className="font-medium">Qualification {index + 1}</h4>
                        {formData.qualifications.length > 1 && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => removeQualification(index)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label>Degree *</Label>
                        <Input
                          value={qualification.degree}
                          onChange={(e) => handleQualificationChange(index, "degree", e.target.value)}
                          placeholder="MBBS"
                          required
                        />
                      </div>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    onClick={addQualification}
                    className="w-full"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Qualification
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Cities</CardTitle>
                  <CardDescription>
                    Cities where the doctor practices
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {formData.cities.map((city, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <Input value={city} readOnly />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => handleArrayField("cities", "remove", null, index)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    <div className="flex gap-2">
                      <Input
                        value={newCity}
                        onChange={(e) => setNewCity(e.target.value)}
                        placeholder="Add city"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          if (newCity.trim()) {
                            handleArrayField("cities", "add", newCity.trim());
                            setNewCity("");
                          }
                        }}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Profile Image</CardTitle>
                  <CardDescription>
                    Upload a profile picture for the doctor
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {imagePreview ? (
                    <div className="space-y-2">
                      <div className="relative w-full aspect-square rounded-lg overflow-hidden border-2 border-gray-200">
                        <img
                          src={imagePreview}
                          alt="Profile preview"
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={removeImage}
                        className="w-full"
                      >
                        <X className="h-4 w-4 mr-2" />
                        Remove Image
                      </Button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center w-full h-48 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors">
                      <div className="flex flex-col items-center justify-center pt-5 pb-6">
                        <ImageIcon className="w-10 h-10 mb-3 text-gray-400" />
                        <p className="mb-2 text-sm text-gray-500">
                          <span className="font-semibold">Click to upload</span> or drag and drop
                        </p>
                        <p className="text-xs text-gray-500">PNG, JPG, GIF up to 5MB</p>
                      </div>
                      <input
                        type="file"
                        className="hidden"
                        accept="image/*"
                        onChange={handleImageChange}
                      />
                    </label>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Doctor Type</CardTitle>
                  <CardDescription>
                    Specify the type of doctor
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="isVisitingDoctor" className="text-sm font-medium">
                      Visiting Doctor
                    </Label>
                    <Switch
                      id="isVisitingDoctor"
                      checked={formData.isVisitingDoctor}
                      onCheckedChange={(checked) => handleInputChange("isVisitingDoctor", checked)}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="isHospitalDoctor" className="text-sm font-medium">
                      Hospital Doctor
                    </Label>
                    <Switch
                      id="isHospitalDoctor"
                      checked={formData.isHospitalDoctor}
                      onCheckedChange={(checked) => handleInputChange("isHospitalDoctor", checked)}
                    />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Actions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4 mr-2" />
                    )}
                    {isLoading ? "Creating Doctor..." : "Create Doctor"}
                  </Button>
                  <Link to="/admin/doctors">
                    <Button variant="outline" className="w-full" disabled={isLoading}>
                      Cancel
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminDoctorCreate;
